export default class CookieManager {
  private readonly container: HTMLDivElement | null
  private readonly cookieNoticeElement: HTMLElement | null
  private readonly settings: HTMLDivElement | null | undefined
  private readonly settingsData: string[] = []
  private data: CookieInformation[] = []
  private removed: number = 0
  private readonly cookieName: string = ''
  private readonly loaded: boolean = false

  constructor () {
    this.cookieNoticeElement = document.querySelector<HTMLElement>('body.nocookies')
    this.container = document.querySelector<HTMLDivElement>('.ext-cookies')
    this.settings = this.container?.querySelector<HTMLDivElement>('.settings')
    const settingsData: string | null | undefined = this.settings?.getAttribute('data-settings')

    if (!this.container || !this.settings || !settingsData) {
      return
    }

    this.settingsData = settingsData.split('-')
    this.cookieName = this.settingsData[2] || '_banner'
    this.loaded = true
  }

  public ajaxLoad (): void {
    if (!this.loaded) {
      return
    }

    const url = `?type=${this.settingsData[1]}&storagePid=${this.settingsData[0]}`
    const XHR = new XMLHttpRequest()
    XHR.addEventListener('load', this.requestListener.bind(this))
    XHR.open('GET', window.location.origin + url)
    XHR.responseType = 'json'
    XHR.send()
  }

  private getDomain (): string {
    const host = window.location.host.split('.')
    return Number(host[host.length - 1]) ? window.location.host : `${host[host.length - 2]}.${host[host.length - 1]}`
  }

  private setCookie (name: string, days: number, domain: string | null): void {
    const date = new Date()
    date.setTime(days ? Date.now() + days * 24 * 60 * 60 * 1000 : 1)

    domain = domain || this.getDomain()
    document.cookie = `${name}=${date.getTime()}; ${days ? `expires=${date.toUTCString()};` : ''} path=/; domain=${domain}; SameSite=Strict`
  }

  private isCookieSet (name: string): boolean {
    let isSet = false

    document.cookie.split(';').forEach(cookie => {
      const cookieParts = cookie.split('=')

      if (name === cookieParts[0].trim()) {
        isSet = true
      }
    })

    return isSet
  }

  private setCheckBoxes (): void {
    this.container?.querySelectorAll('.cookies-cookie').forEach(cookieElement => {
      const input = cookieElement.firstElementChild?.firstElementChild as HTMLInputElement

      if (input && window.localStorage.getItem(input.id)) {
        input.checked = true
      }
    })
  }

  private getInlineScripts (data: CookieInformation): NodeListOf<HTMLScriptElement> {
    const script = data.script
    const inlineData = document.createElement('div')
    inlineData.innerHTML = script

    return inlineData.querySelectorAll('script')
  }

  private getScriptId (input: HTMLInputElement): string {
    return `${input.id}_${input.getAttribute('data-cuid')}`
  }

  private setScripts (data: CookieInformation, input: HTMLInputElement): void {
    this.getInlineScripts(data).forEach(inlineScript => {
      const script = document.createElement('script')
      script.id = this.getScriptId(input)

      if (inlineScript.hasAttribute('src')) {
        script.src = inlineScript.src
      }

      script.innerHTML = inlineScript.innerHTML
      document.body.appendChild(script)
    })
  }

  private removeScripts (data: CookieInformation, input: HTMLInputElement): void {
    this.getInlineScripts(data).forEach(() => {
      const id = this.getScriptId(input)
      document.querySelector(`#${id}`)?.remove()
    })
  }

  private findSingleData (input: HTMLInputElement): CookieInformation | null {
    const inputAttribute = input.getAttribute('data-cuid')?.toString()

    return this.data.find(item => item.uid.toString() === inputAttribute) ?? null
  }

  private removeCookieFromLocalStorage (input: HTMLInputElement, data: CookieInformation): void {
    if (window.localStorage.getItem(input.id)) {
      this.removed += 1
      window.localStorage.removeItem(input.id)
      this.removeScripts(data, input)
    }
  }

  private addCookieToLocalStorage (input: HTMLInputElement, data: CookieInformation): void {
    if (!window.localStorage.getItem(input.id)) {
      window.localStorage.setItem(input.id, Date.now().toString())
      this.setScripts(data, input)
    }
  }

  private setCheckedCookies (): void {
    this.container?.querySelectorAll<HTMLDivElement>('.cookies-cookie').forEach(cookie => {
      const input = cookie.firstElementChild?.firstElementChild

      if (!(input instanceof HTMLInputElement)) {
        return
      }

      const data = this.findSingleData(input)

      if (!data) {
        return
      }

      const dataCookies = data.name.split(',')

      if (input.checked) {
        this.addCookieToLocalStorage(input, data)
        return
      }

      this.removeCookieFromLocalStorage(input, data)

      dataCookies.forEach(dataCookie => {
        this.setCookie(dataCookie.trim(), -1, null)
      })
    })
  }

  private setAllCookies (): void {
    this.container?.querySelectorAll<HTMLDivElement>('.cookies-cookie').forEach(cookie => {
      const input = cookie.firstElementChild?.firstElementChild

      if (!(input instanceof HTMLInputElement)) {
        return
      }

      const data = this.findSingleData(input)

      if (!data) {
        return
      }

      input.checked = true

      this.addCookieToLocalStorage(input, data)
    })
  }

  private removeAllCookies (): void {
    this.container?.querySelectorAll<HTMLDivElement>('.cookies-cookie').forEach(cookie => {
      const input = cookie.firstElementChild?.firstElementChild

      if (!(input instanceof HTMLInputElement)) {
        return
      }

      const data = this.findSingleData(input)

      if (!data) {
        return
      }

      const dataCookies = data.name.split(',')
      this.removeCookieFromLocalStorage(input, data)

      dataCookies.forEach(dataCookie => {
        this.setCookie(dataCookie.trim(), -1, null)
      })
    })
  }

  private initCookies (): void {
    if (!this.container) {
      return
    }

    const cookieContainer = this.container.querySelector<HTMLDivElement>('.cookies-container')
    const cookies = this.container.querySelectorAll<HTMLDivElement>('.cookies-cookie')
    const overlay = document.querySelector<HTMLDivElement>('.cookies-overlay')
    const buttonFingerPrint = document.querySelector<HTMLDivElement>('.cookies-fingerprint-btn')
    const buttonAllCookies = this.container.querySelector<HTMLButtonElement>('#cookies-all-btn')
    const buttonSaveCookies = this.container.querySelector<HTMLButtonElement>('#cookies-save-btn')
    const buttonDeclineCookies = this.container.querySelector<HTMLButtonElement>('#cookies-decline-btn')

    const handleClick = (action: Function): void => {
      window.localStorage.setItem(this.cookieName, Date.now().toString())
      action()
      cookieContainer?.classList.add('cookies-hidden')
      buttonFingerPrint?.classList.remove('mgc:hidden')
      overlay?.classList.add('mgc:hidden')

      if (this.removed > 0) {
        window.location.reload()
      }
    }

    buttonAllCookies?.addEventListener('click', () => handleClick(this.setAllCookies.bind(this)))
    buttonSaveCookies?.addEventListener('click', () => handleClick(this.setCheckedCookies.bind(this)))
    buttonDeclineCookies?.addEventListener('click', () => handleClick(this.removeAllCookies.bind(this)))

    if (buttonFingerPrint) {
      buttonFingerPrint.addEventListener('click', () => {
        const toggleVisibility = cookieContainer?.classList.contains('cookies-hidden') ?? false
        this.setCheckBoxes()
        cookieContainer?.classList.toggle('cookies-hidden', !toggleVisibility)
        buttonFingerPrint.classList.toggle('mgc:hidden', toggleVisibility)
        overlay?.classList.toggle('mgc:hidden', !toggleVisibility)
      })
    }

    const handleCookieInfoToggle = (cookieInfoButton: HTMLSpanElement | null, cookieInfoText: HTMLDivElement | null): void => {
      if (cookieInfoButton && cookieInfoText) {
        cookieInfoButton.addEventListener('click', () => {
          cookieInfoText.classList.toggle('mgc:hidden')
        })
      }
    }

    const handleCookie = (cookie: HTMLDivElement) => {
      const cookieInfoButton = cookie.querySelector<HTMLSpanElement>('.cookie-info-btn')
      const cookieInfoText = cookie.querySelector<HTMLDivElement>('.cookies-info-text')

      handleCookieInfoToggle(cookieInfoButton, cookieInfoText)

      const input = cookie.firstElementChild?.firstElementChild

      if (!(input instanceof HTMLInputElement)) {
        return
      }

      const data = this.findSingleData(input)

      if (!data) {
        return
      }

      const dataCookies = data.name.split(',')
      const noscriptDiv = document.createElement('div')
      const dataNoScript = data.noscript

      if (dataNoScript.length > 0) {
        noscriptDiv.innerHTML = dataNoScript
        const innerNoScript = noscriptDiv.querySelector('noscript')

        if (innerNoScript) {
          const noscript = document.createElement('noscript')
          noscript.id = `${input.id}_noscript`
          noscript.innerHTML = innerNoScript.innerHTML
          document.body.appendChild(noscript)
        }
      }

      if (window.localStorage.getItem(input.id)) {
        input.checked = true
        this.setScripts(data, input)
      } else {
        this.removeScripts(data, input)

        dataCookies.forEach(dataCookie => {
          this.setCookie(dataCookie.trim(), -1, null)
        })
      }
    }

    cookies.forEach(handleCookie)

    console.log(this.cookieNoticeElement)

    if (!window.localStorage.getItem(this.cookieName) && !this.cookieNoticeElement) {
      cookieContainer?.classList.remove('cookies-hidden')
      buttonFingerPrint?.classList.add('mgc:hidden')
      overlay?.classList.remove('mgc:hidden')
    }
  }

  private requestListener (event: ProgressEvent): void {
    const response = event.target as XMLHttpRequest

    if (response.status === 200) {
      this.data = response.response
      this.initCookies()
    }
  }
}

type CookieInformation = {
  uid: number,
  title: string,
  state: number,
  name: string,
  description: string,
  script: string,
  noscript: string,
}